import React, { useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { useThemeMode } from '../../hooks/useThemeMode';

const stats = [
  { label: 'Chains', value: 6, suffix: '', sub: 'All Ecosystems' },
  { label: 'Tokens', value: 10, suffix: '+', sub: 'All Majors' },
  { label: 'Privacy', value: 100, suffix: '%', sub: 'Guaranteed' },
  { label: 'Uptime', value: 99, suffix: '.9%', sub: 'Network Status' },
];

const Counter = ({ value, suffix, isDark }) => {
  const ref = useRef(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { damping: 30, stiffness: 100 });
  const isInView = useInView(ref, { once: true, margin: "-10px" });

  useEffect(() => {
    if (isInView) {
      motionValue.set(value);
    }
  }, [isInView, value, motionValue]);

  useEffect(() => {
    return springValue.on("change", (latest) => {
      if (ref.current) {
        ref.current.textContent = Math.floor(latest).toString() + suffix;
      }
    });
  }, [springValue, suffix]);

  return <span ref={ref} className={`text-3xl font-mono font-bold mb-1 ${isDark ? 'text-white' : 'text-neutral-900'}`} />;
};

const StatsBar = () => {
  const { isDark } = useThemeMode();

  return (
    <div
      className={`border-y ${isDark ? 'border-neutral-900' : 'border-neutral-200'}`}
      style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', backdropFilter: 'blur(4px)' }}
    >
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="text-center md:text-left"
            >
              <div className="h-9 mb-1 flex justify-center md:justify-start">
                <Counter value={stat.value} suffix={stat.suffix} isDark={isDark} />
              </div>
              <div className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>{stat.label}</div>
              <div className={`text-xs mt-1 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>{stat.sub}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatsBar;
